import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * XEGA — Spectacular intro animation.
 *
 * Sequence:
 *  0 ambient (deep black)
 *  1 grid + scan lines + corner brackets + telemetry labels
 *  2 orbits of satellites + location pins + connecting lines pulse around the centre
 *  3 orbits collapse inward and the XEGA wordmark is "traced" by the same cyan light,
 *    then filled with a chrome/metallic gradient (the logo IS the lines).
 *  4 outro fade -> reveal the main site.
 *
 * Persisted in sessionStorage so it only plays once per session.
 */
export default function IntroAnimation({ onDone }) {
  const [visible, setVisible] = useState(() => !localStorage.getItem("xega_intro_shown"));
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!visible) { onDone?.(); return; }
    const timers = [
      setTimeout(() => setPhase(1), 250),    // lines/grid emerge
      setTimeout(() => setPhase(2), 1100),   // satellites + pins
      setTimeout(() => setPhase(3), 2500),   // collapse + draw logo
      setTimeout(() => setPhase(4), 7000),   // outro fade (after long dwell on chrome logo)
      setTimeout(() => {
        localStorage.setItem("xega_intro_shown", "1");
        setVisible(false);
        onDone?.();
      }, 8100),
    ];
    return () => timers.forEach(clearTimeout);
  }, [visible, onDone]);

  const skip = () => {
    localStorage.setItem("xega_intro_shown", "1");
    setVisible(false);
    onDone?.();
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="intro"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        animate={{ opacity: phase >= 4 ? 0 : 1 }}
        transition={{ duration: 1.1, ease: "easeInOut" }}
        className="fixed inset-0 z-[100] bg-[#06080D] overflow-hidden flex items-center justify-center"
        data-testid="intro-animation"
      >
        <style>{`
          @keyframes intro-scan-h { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
          @keyframes intro-scan-v { 0% { transform: translateY(-100%); } 100% { transform: translateY(100%); } }
          @keyframes intro-grid-fade { 0% { opacity: 0; } 50% { opacity: 0.55; } 100% { opacity: 0.18; } }
          @keyframes intro-orbit  { from { transform: rotateZ(0deg); }   to { transform: rotateZ(360deg); } }
          @keyframes intro-orbit-r{ from { transform: rotateZ(360deg); } to { transform: rotateZ(0deg); } }
          @keyframes intro-shimmer-x { 0% { transform: translateX(-140%) skewX(-22deg); } 60%,100% { transform: translateX(160%) skewX(-22deg); } }
          @keyframes intro-chrome-glow {
            0%,100% { filter: drop-shadow(0 0 14px rgba(0,240,255,0.30)) drop-shadow(0 0 36px rgba(255,255,255,0.10)) drop-shadow(0 22px 36px rgba(0,0,0,0.6)); }
            50%    { filter: drop-shadow(0 0 26px rgba(0,240,255,0.55)) drop-shadow(0 0 60px rgba(255,255,255,0.22)) drop-shadow(0 22px 36px rgba(0,0,0,0.6)); }
          }
          @keyframes intro-flash-burst {
            0%   { opacity: 0;    transform: translate(-50%,-50%) scale(0.2); }
            30%  { opacity: 0.95; transform: translate(-50%,-50%) scale(1.1); }
            100% { opacity: 0;    transform: translate(-50%,-50%) scale(2.4); }
          }
          @keyframes intro-sparkle {
            0%, 100% { opacity: 0; transform: translate(-50%,-50%) scale(0.2) rotate(0deg); }
            45%      { opacity: 1; transform: translate(-50%,-50%) scale(1)   rotate(35deg); }
            55%      { opacity: 1; transform: translate(-50%,-50%) scale(1.1) rotate(45deg); }
          }
          @keyframes intro-logo-float {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            50%      { transform: translateY(-3px) rotate(-0.25deg); }
          }
        `}</style>

        {/* ambient grid + radial glow + noise */}
        <div className="absolute inset-0 grid-bg" style={{ animation: 'intro-grid-fade 1.2s ease-out forwards' }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,240,255,0.10),transparent_65%)]" />
        <div className="absolute inset-0 noise-overlay opacity-60" />

        {/* full-screen scan lines (during phases 1-2) */}
        {phase >= 1 && phase < 3 && (
          <>
            <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-300 to-transparent top-1/2"
              style={{ animation: 'intro-scan-h 1.6s ease-in-out infinite', boxShadow: '0 0 24px #00F0FF' }} />
            <div className="absolute inset-y-0 w-px bg-gradient-to-b from-transparent via-cyan-300 to-transparent left-1/2"
              style={{ animation: 'intro-scan-v 1.6s ease-in-out infinite 0.2s', boxShadow: '0 0 24px #00F0FF' }} />
          </>
        )}

        {/* corner brackets */}
        {[0, 1, 2, 3].map((i) => {
          const positions = [
            "top-6 left-6 border-l-2 border-t-2 rounded-tl-md",
            "top-6 right-6 border-r-2 border-t-2 rounded-tr-md",
            "bottom-6 left-6 border-l-2 border-b-2 rounded-bl-md",
            "bottom-6 right-6 border-r-2 border-b-2 rounded-br-md",
          ];
          return (
            <motion.div key={i}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: phase >= 1 ? 1 : 0, scale: phase >= 1 ? 1 : 0.6 }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className={`absolute w-8 h-8 sm:w-12 sm:h-12 border-cyan-300/80 ${positions[i]}`} />
          );
        })}

        {/* telemetry labels */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase >= 1 && phase < 4 ? 1 : 0 }} transition={{ delay: 0.5 }}
          className="absolute top-8 left-16 sm:top-10 sm:left-24 label-mono !text-cyan-300 text-[9px] sm:text-[10px]">
          INICIANDO SISTEMA · v2.6
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase >= 1 && phase < 4 ? 1 : 0 }} transition={{ delay: 0.65 }}
          className="absolute top-8 right-16 sm:top-10 sm:right-24 label-mono text-[9px] sm:text-[10px]">
          SAT-LINK · 06/12
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase >= 2 && phase < 4 ? 1 : 0 }} transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-16 sm:bottom-10 sm:left-24 label-mono text-[9px] sm:text-[10px]">
          N 19.4326° · W 99.1332°
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase >= 2 && phase < 4 ? 1 : 0 }} transition={{ delay: 1.35 }}
          className="absolute bottom-8 right-16 sm:bottom-10 sm:right-24 label-mono !text-emerald-300 text-[9px] sm:text-[10px]">
          UPLINK ✓ READY
        </motion.div>

        {/* === Central stage === */}
        <div className="relative w-[92vw] sm:w-[560px] md:w-[680px] max-w-[92vw] aspect-square flex items-center justify-center">

          {/* Orbits — visible in phases 1-2, then COLLAPSE inward in phase 3 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{
              opacity: phase >= 1 ? (phase < 3 ? 1 : 0) : 0,
              scale:   phase >= 3 ? 0.15 : 1,
              rotate:  phase >= 3 ? 90 : 0,
            }}
            transition={{ duration: phase >= 3 ? 0.9 : 0.8, ease: [0.7, 0, 0.3, 1] }}
            className="absolute inset-0 rounded-full border border-cyan-300/35"
            style={{ animation: phase < 3 ? 'intro-orbit 7s linear infinite' : undefined }}>
            <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-300 shadow-[0_0_18px_5px_rgba(0,240,255,0.7)]" />
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 rounded-full bg-cyan-300/70" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: phase >= 1 ? (phase < 3 ? 1 : 0) : 0,
              scale:   phase >= 3 ? 0.18 : 1,
              rotate:  phase >= 3 ? -120 : 0,
            }}
            transition={{ duration: phase >= 3 ? 0.9 : 0.8, delay: 0.05, ease: [0.7, 0, 0.3, 1] }}
            className="absolute inset-[10%] rounded-full border border-emerald-300/25"
            style={{ animation: phase < 3 ? 'intro-orbit-r 5s linear infinite' : undefined }}>
            <span className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 w-2.5 h-2.5 rounded-full bg-emerald-300 shadow-[0_0_14px_4px_rgba(16,255,128,0.6)]" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{
              opacity: phase >= 1 ? (phase < 3 ? 1 : 0) : 0,
              scale:   phase >= 3 ? 0.2 : 1,
              rotate:  phase >= 3 ? 180 : 0,
            }}
            transition={{ duration: phase >= 3 ? 0.9 : 0.8, delay: 0.1, ease: [0.7, 0, 0.3, 1] }}
            className="absolute inset-[24%] rounded-full border border-white/20"
            style={{ animation: phase < 3 ? 'intro-orbit 3.5s linear infinite' : undefined }}>
            <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_10px_3px_rgba(255,255,255,0.7)]" />
          </motion.div>

          {/* Location pins and connecting lines (phase 2 only) */}
          {phase >= 2 && phase < 3 && (
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full pointer-events-none">
              {[
                { x: 60, y: 70, d: 0 },
                { x: 145, y: 60, d: 0.2 },
                { x: 150, y: 130, d: 0.4 },
                { x: 65, y: 140, d: 0.55 },
                { x: 100, y: 50, d: 0.7 },
                { x: 50, y: 100, d: 0.9 },
              ].map((p, i) => (
                <g key={i} style={{ opacity: 1, transition: 'opacity 0.4s', transitionDelay: `${p.d}s` }}>
                  <path d={`M ${p.x} ${p.y} l -4 -7 a 5 5 0 1 1 8 0 z`} fill="#00F0FF" opacity="0.9" />
                  <circle cx={p.x} cy={p.y - 8} r="1.6" fill="#06080D" />
                  <circle cx={p.x} cy={p.y} r="10" fill="none" stroke="#00F0FF" strokeOpacity="0.6">
                    <animate attributeName="r" from="4" to="22" dur="2s" begin={`${p.d}s`} repeatCount="indefinite" />
                    <animate attributeName="opacity" from=".7" to="0" dur="2s" begin={`${p.d}s`} repeatCount="indefinite" />
                  </circle>
                </g>
              ))}
              <g stroke="#00F0FF" strokeOpacity="0.25" strokeDasharray="2 4">
                {[[60, 70], [145, 60], [150, 130], [65, 140]].map(([x, y], i) => (
                  <line key={i} x1="100" y1="100" x2={x} y2={y} />
                ))}
              </g>
            </svg>
          )}

          {/* === CHROME LOGO REVEAL (phase 3+) ===
              The XEGA wordmark is drawn with the SAME cyan stroke the orbits had,
              then filled with a chrome gradient that shimmers. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: phase >= 3 ? 1 : 0, scale: phase >= 3 ? 1 : 0.85 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-[88%] sm:w-[78%] flex flex-col items-center justify-center"
            style={{ animation: phase >= 3 ? 'intro-chrome-glow 2.6s ease-in-out infinite' : undefined }}>

            <div className="relative w-full">
              {/* Shimmer sweep on top of the chrome fill */}
              {phase >= 3 && (
                <div className="pointer-events-none absolute inset-0 overflow-hidden mix-blend-screen">
                  <div
                    className="absolute top-0 -left-1/3 h-full w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent skew-x-[-20deg]"
                    style={{ animation: 'intro-shimmer-x 2.8s ease-in-out 0.6s infinite' }}
                  />
                </div>
              )}

              <svg viewBox="0 0 800 220" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
                <defs>
                  <linearGradient id="xegaChrome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#FFFFFF" />
                    <stop offset="22%" stopColor="#E6ECF2" />
                    <stop offset="45%" stopColor="#A6B0BC" />
                    <stop offset="55%" stopColor="#5C6571" />
                    <stop offset="72%" stopColor="#C7CFD8" />
                    <stop offset="100%" stopColor="#FFFFFF" />
                  </linearGradient>
                  <linearGradient id="xegaEdge" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%"  stopColor="#00F0FF" stopOpacity="0.9" />
                    <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#10FF80" stopOpacity="0.85" />
                  </linearGradient>
                  <filter id="xegaBlur" x="-10%" y="-30%" width="120%" height="160%">
                    <feGaussianBlur stdDeviation="2.2" />
                  </filter>
                </defs>

                {phase >= 3 && (
                  <text
                    x="400" y="148" textAnchor="middle"
                    fontFamily="'Orbitron','Rajdhani',sans-serif"
                    fontWeight="900" fontSize="180" letterSpacing="14"
                    fill="#00F0FF" opacity="0.35" filter="url(#xegaBlur)">
                    XEGA
                  </text>
                )}

                <text
                  x="400" y="148" textAnchor="middle"
                  fontFamily="'Orbitron','Rajdhani',sans-serif"
                  fontWeight="900" fontSize="180" letterSpacing="14"
                  stroke="url(#xegaEdge)" strokeWidth="2"
                  fill="url(#xegaChrome)"
                  fillOpacity={phase >= 3 ? undefined : 0}
                  style={{
                    strokeDasharray: 1400,
                    strokeDashoffset: phase >= 3 ? 0 : 1400,
                    transition: 'stroke-dashoffset 1.6s cubic-bezier(.16,1,.3,1), fill-opacity 1s ease 0.8s',
                    fillOpacity: phase >= 3 ? 1 : 0,
                    paintOrder: 'stroke',
                  }}>
                  XEGA
                </text>

                <g stroke="url(#xegaEdge)" strokeWidth="1.5" strokeLinecap="round" opacity={phase >= 3 ? 1 : 0}
                   style={{ transition: 'opacity 0.6s ease 1.2s' }}>
                  <line x1="40"  y1="148" x2="170" y2="148" />
                  <line x1="630" y1="148" x2="760" y2="148" />
                  <circle cx="32"  cy="148" r="3" fill="#00F0FF" />
                  <circle cx="768" cy="148" r="3" fill="#10FF80" />
                </g>

                <text x="400" y="195" textAnchor="middle"
                      fontFamily="'Rajdhani',sans-serif" fontWeight="500"
                      fontSize="20" letterSpacing="10"
                      fill="#9BE7FF" opacity={phase >= 3 ? 0.9 : 0}
                      style={{ transition: 'opacity 0.8s ease 1.5s' }}>
                  ALTA TECNOLOGÍA EN SEGURIDAD
                </text>
              </svg>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: phase >= 3 ? 1 : 0, y: phase >= 3 ? 0 : 10 }}
              transition={{ delay: 1.9, duration: 0.7 }}
              className="mt-2 sm:mt-3 flex items-center justify-center gap-3">
              <span className="h-px w-8 sm:w-14 bg-gradient-to-r from-transparent to-white/50" />
              <span className="label-mono !text-cyan-300/95 !tracking-[0.35em] text-[10px] sm:text-xs">
                CONTROL TOTAL EN TIEMPO REAL
              </span>
              <span className="h-px w-8 sm:w-14 bg-gradient-to-r from-white/50 to-transparent" />
            </motion.div>
          </motion.div>
        </div>

        {/* Loading bar at the bottom */}
        <div className="absolute bottom-16 sm:bottom-20 inset-x-0 flex flex-col items-center gap-2">
          <div className="h-[2px] w-40 sm:w-56 bg-white/10 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 7.2, ease: "easeInOut" }}
              className="h-full bg-gradient-to-r from-cyan-300 via-white to-emerald-300 shadow-[0_0_10px_rgba(0,240,255,0.6)]" />
          </div>
          <span className="label-mono text-white/40 text-[9px]">CARGANDO EXPERIENCIA · XEGA</span>
        </div>

        {/* Skip button */}
        <button onClick={skip} data-testid="intro-skip"
          className="absolute top-4 right-4 sm:top-6 sm:right-6 px-3 py-1.5 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-cyan-400/50 label-mono text-[10px] transition z-20">
          SALTAR
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
